import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, AlertTriangle, Inbox } from 'react-feather';

/** Format a numeric money value with thousands separators + 2 decimals. */
export const formatMoney = (v) => {
    const n = Number(v);
    if (v == null || v === '' || Number.isNaN(n)) return '';
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Format an integer count with thousands separators. */
export const formatCount = (v) => {
    const n = Number(v);
    if (v == null || v === '' || Number.isNaN(n)) return v ?? '';
    return n.toLocaleString();
};

/**
 * V3 data table for reports.
 *
 *   columns  — [{ key, label, align?, isMoney?, sortable?(default true),
 *               render?(item), format?(value) }]
 *   rows     — current page rows already sorted/sliced by the caller
 *   sort     — { key, dir: 'asc' | 'desc' }
 *   onSort   — (key) => void
 *   totals   — optional { [columnKey]: value } row rendered in tfoot
 *   loading  — show skeleton
 *   error    — string; show error state
 *   emptyMessage — shown when rows is empty and not loading/error
 *   skeletonRows — rows of skeleton to render while loading (default 8)
 */
const ReportTable = ({
    columns = [],
    rows = [],
    sort = { key: '', dir: 'asc' },
    onSort,
    totals = null,
    loading = false,
    error = '',
    emptyMessage = '',
    skeletonRows = 8,
    rowClassName,
}) => {
    const { t } = useTranslation();
    const cellAlign = (col) => (col.isMoney || col.align === 'right' ? 'num' : '');

    const renderCell = (col, item) => {
        if (col.render) return col.render(item);
        const raw = item[col.key];
        if (col.format) return col.format(raw);
        if (col.isMoney) return formatMoney(raw);
        return raw;
    };

    const sortIcon = (col) => {
        if (col.sortable === false) return null;
        const active = sort.key === col.key;
        if (!active) {
            return <ChevronUp size={12} className="v3-sort-ico idle" />;
        }
        return sort.dir === 'asc'
            ? <ChevronUp size={12} className="v3-sort-ico" />
            : <ChevronDown size={12} className="v3-sort-ico" />;
    };

    // ── Non-data states ──────────────────────────────────────────────
    if (error) {
        return (
            <div className="v3-card v3-report-table-card">
                <div className="v3-report-state error">
                    <span className="v3-report-state-icon"><AlertTriangle size={20} /></span>
                    <h3>{t('Couldn’t load this report')}</h3>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="v3-card v3-report-table-card">
            <div className="v3-sadmin-table-wrap">
                <table className="v3-sadmin-table v3-report-table">
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className={`${cellAlign(col)} ${col.sortable === false ? '' : 'sortable'}`.trim()}
                                    onClick={col.sortable === false ? undefined : () => onSort?.(col.key)}
                                >
                                    {t(col.label)}{sortIcon(col)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            Array.from({ length: skeletonRows }).map((_, r) => (
                                <tr className="v3-report-skel-row" key={`skel-${r}`}>
                                    {columns.map((col) => (
                                        <td key={col.key}>
                                            <div className="v3-report-skel-bar" style={{ width: `${40 + ((r + col.key.length) % 5) * 12}%` }} />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length}>
                                    <div className="v3-report-state">
                                        <span className="v3-report-state-icon"><Inbox size={20} /></span>
                                        <h3>{t('No results')}</h3>
                                        <p>{emptyMessage || t('No data for the selected filters. Try widening the date range or clearing filters.')}</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            rows.map((item, idx) => (
                                <tr key={item._id || item.id || idx} className={rowClassName ? rowClassName(item) : undefined}>
                                    {columns.map((col) => (
                                        <td key={col.key} className={cellAlign(col)}>
                                            {renderCell(col, item)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                    {totals && !loading && rows.length > 0 && (
                        <tfoot>
                            <tr>
                                {columns.map((col, i) => {
                                    const has = Object.prototype.hasOwnProperty.call(totals, col.key);
                                    let content = '';
                                    if (has) content = col.isMoney ? formatMoney(totals[col.key]) : (col.format ? col.format(totals[col.key]) : totals[col.key]);
                                    else if (i === 0) content = t('Total');
                                    return (
                                        <td key={col.key} className={cellAlign(col)}>{content}</td>
                                    );
                                })}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};

export default ReportTable;
